import { useEffect, useMemo, useRef, useState } from "react";
import {
  clampOffset,
  CropRegion,
  fitScale,
  regionFromViewport,
  Size,
} from "../utils/ImageCrop";
import "./ImageCropper.css";

export interface ImageCropperProps {
  /** Object URL (blob:) of the image being positioned. */
  imageUrl: string;
  /** width / height of the framed viewport (e.g. 2400/812). */
  aspectRatio: number;
  onCropChange: (region: CropRegion) => void;
}

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

const MAX_ZOOM = 4;

const ImageCropper = ({
  imageUrl,
  aspectRatio,
  onCropChange,
}: ImageCropperProps) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const centeredRef = useRef(false);

  const [viewport, setViewport] = useState<Size | null>(null);
  const [natural, setNatural] = useState<Size | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Reset geometry whenever a new image is picked.
  useEffect(() => {
    centeredRef.current = false;
    setNatural(null);
    setZoom(1);
  }, [imageUrl]);

  // The viewport's rendered pixel size is needed to translate the on-screen
  // frame back into natural-image coordinates.
  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return;
    const observer = new ResizeObserver(() => {
      setViewport({ width: node.clientWidth, height: node.clientHeight });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const rendered = useMemo(() => {
    if (!natural || !viewport) return null;
    const scale = fitScale(viewport, natural) * zoom;
    return {
      scale,
      width: natural.width * scale,
      height: natural.height * scale,
    };
  }, [natural, viewport, zoom]);

  // Center the image when it first loads; afterwards keep offsets clamped
  // (viewport resize, zoom changes) without jumping back to center.
  useEffect(() => {
    if (!viewport || !rendered) return;
    setOffset((prev) => {
      const next = centeredRef.current
        ? prev
        : {
            x: (viewport.width - rendered.width) / 2,
            y: (viewport.height - rendered.height) / 2,
          };
      centeredRef.current = true;
      return clampOffset(next, viewport, rendered);
    });
  }, [viewport, rendered]);

  // Report the framed region to the parent whenever geometry changes —
  // including the initial centered crop right after mount.
  useEffect(() => {
    if (!viewport || !rendered) return;
    onCropChange(regionFromViewport(viewport, offset, rendered.scale));
  }, [viewport, rendered, offset, onCropChange]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setNatural({ width: img.naturalWidth, height: img.naturalHeight });
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    viewportRef.current?.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId || !viewport || !rendered) {
      return;
    }
    setOffset(
      clampOffset(
        {
          x: drag.offsetX + (e.clientX - drag.startX),
          y: drag.offsetY + (e.clientY - drag.startY),
        },
        viewport,
        rendered,
      ),
    );
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  };

  // Keyboard nudging: arrow keys move the image by 1px (10px with Shift).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!viewport || !rendered) return;
    const step = e.shiftKey ? 10 : 1;
    const delta: { [key: string]: { dx: number; dy: number } } = {
      ArrowLeft: { dx: -step, dy: 0 },
      ArrowRight: { dx: step, dy: 0 },
      ArrowUp: { dx: 0, dy: -step },
      ArrowDown: { dx: 0, dy: step },
    };
    const move = delta[e.key];
    if (!move) return;
    e.preventDefault();
    setOffset(
      clampOffset(
        { x: offset.x + move.dx, y: offset.y + move.dy },
        viewport,
        rendered,
      ),
    );
  };

  return (
    <div
      className="image-cropper"
      style={{ "--crop-ratio": String(aspectRatio) } as React.CSSProperties}
    >
      <div
        ref={viewportRef}
        className="image-cropper__viewport"
        aria-label="Image crop area — drag to position, arrow keys to nudge"
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onKeyDown={handleKeyDown}
      >
        <img
          className="image-cropper__image"
          src={imageUrl}
          alt=""
          draggable={false}
          onLoad={handleImageLoad}
          style={
            // Hidden until geometry is known so the image doesn't flash
            // at its natural size inside the frame.
            rendered
              ? {
                  width: rendered.width,
                  height: rendered.height,
                  left: offset.x,
                  top: offset.y,
                }
              : { visibility: "hidden" }
          }
        />
        <div className="image-cropper__frame" aria-hidden="true" />
      </div>
      <label className="image-cropper__controls">
        <span className="image-cropper__controls-label">Zoom</span>
        <input
          type="range"
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
        />
      </label>
    </div>
  );
};

export default ImageCropper;
